import cx from 'classnames';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {MediaDeviceWrapper, notifier} from 'components';
import {DeviceKind} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {logger, mediaDevicesUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.MEDIA_DEVICES_MODAL;

type Props = {
    value: string;
    devices: MediaDeviceInfo[];
    onChange: (deviceId: string) => void
};

export const VideoInputDevice: React.FC<Props> = ({value, devices, onChange}) => {
    const {t} = useTranslation();

    const videoRef = useRef<HTMLVideoElement>(null);
    const [videoStream, setVideoStream] = useState(null);

    const requestDevicePermissions = useCallback(() =>
        navigator.mediaDevices.getUserMedia({
            video: {deviceId: {ideal: value}},
            audio: false
        }).then(setVideoStream).catch((error) => {
            logger.error(error);
            notifier.error({title: t(T_KEYS.ERROR_TITLE), details: error.message});
        }),
    [t, value]);

    const handleChangeVideoInputDevice = useCallback((deviceId: string) => {
        onChange(deviceId);
        setVideoStream(null);
    }, [onChange]);

    useEffect(() => {
        if (!videoStream) {
            requestDevicePermissions();
        }
    }, [videoStream, requestDevicePermissions]);

    useEffect(() => {
        if (videoStream) {
            try {
                const videoElement = videoRef.current;
                if ('srcObject' in videoElement) {
                    videoElement.srcObject = videoStream;
                }
            } catch (error: any) {
                notifier.error({title: t(T_KEYS.CAMERA_ERROR), details: error?.message});
            }

        }
    }, [videoStream, t]);

    useEffect(() => () => {
        mediaDevicesUtils.cleanMediaStream(videoStream);
    }, [videoStream]);

    return (
        <MediaDeviceWrapper
            type={DeviceKind.Camera}
            devices={devices}
            selectedDeviceId={value}
            onDeviceChange={handleChangeVideoInputDevice}
        >
            <video
                ref={videoRef}
                className={cx('tw-text-center tw-mt-4', {'tw-w-0': !videoStream, 'tw-w-fit': !!videoStream})}
                autoPlay
                playsInline
                muted
            />
        </MediaDeviceWrapper>
    );
};